// frontend/src/services/internationalTransferService.js - UPDATED WITH VERIFICATION
import api from './api';

const internationalTransferService = {
  // ============ VERIFICATION ENDPOINTS ============
  checkVerificationRequired: async (transferType = 'international', amount = 0) => {
    console.log(`Checking verification for ${transferType} transfer of $${amount}`);
    try {
      const response = await api.post('/banking/verification/check-required/', {
        type: transferType,
        amount: amount
      });
      console.log('Verification check:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking verification:', error.response?.data || error.message);
      // Default to allowing if endpoint fails
      return {
        can_proceed: true,
        message: 'Verification check failed, proceeding anyway',
        needs_verification: false,
        current_step: 6,
        is_fully_verified: true
      };
    }
  },

  getVerificationStatus: async () => {
    try {
      const response = await api.get('/banking/verification/status/');
      return response.data;
    } catch (error) {
      console.error('Error fetching verification status:', error);
      // Return default status
      return {
        is_verification_completed: false,
        current_step: 1,
        next_required_step: 1,
        completed_steps: [],
        remaining_steps: [1, 2, 3, 4, 5],
        progress_percentage: 0,
        has_active_code: false,
        total_steps: 5
      };
    }
  },

  verifyAccountStep: async (code) => {
    console.log('Verifying step with code:', code);
    try {
      const response = await api.post('/banking/verification/verify-step/', {
        code: code
      });
      console.log('Verification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error verifying step:', error.response?.data || error.message);
      throw error;
    }
  },

  getCurrentVerificationCodeInfo: async () => {
    try {
      const response = await api.get('/banking/verification/current-code/');
      return response.data;
    } catch (error) {
      console.error('Error fetching code info:', error);
      return {
        has_active_code: false,
        message: 'Unable to fetch code information'
      };
    }
  },

  // ============ INTERNATIONAL TRANSFER ENDPOINTS ============
  createInternationalTransfer: async (transferData) => {
    console.log('Creating international transfer:', transferData);
    
    // First check if verification is required
    try {
      const verificationCheck = await internationalTransferService.checkVerificationRequired(
        'international', 
        transferData.amount
      );
      
      if (!verificationCheck.can_proceed) {
        throw {
          response: {
            data: {
              error: 'Verification required',
              message: verificationCheck.message,
              needs_verification: true,
              current_step: verificationCheck.current_step
            }
          }
        };
      }
    } catch (error) {
      // If verification check itself fails, still try to create transfer
      console.warn('Verification check failed, proceeding with transfer:', error.message);
    }
    
    // Proceed with transfer creation
    try {
      const response = await api.post('/banking/international-transfers/', transferData);
      console.log('Transfer created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating transfer:', error.response?.data || error.message);
      throw error;
    }
  },

  getMyInternationalTransfers: async () => {
    try {
      const response = await api.get('/banking/international-transfers/');
      // Handle paginated response
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching transfers:', error);
      return [];
    }
  },

  getInternationalTransferDetail: async (transferId) => {
    try {
      const response = await api.get(`/banking/international-transfers/${transferId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer detail:', error);
      throw error;
    }
  },

  // OLD verification (keep for backward compatibility)
  verifyInternationalTransferCode: async (transferId, step, code) => {
    console.log(`Verifying code for transfer ${transferId}, step ${step}`);
    try {
      const response = await api.post('/banking/international-transfers/verify/', {
        transfer_id: transferId,
        step: step,
        code: code,
      });
      console.log('Verification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error verifying code:', error.response?.data || error.message);
      throw error;
    }
  },

  getInternationalTransferStatus: async (transferId) => {
    try {
      const response = await api.get(`/banking/international-transfers/${transferId}/status/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer status:', error);
      throw error;
    }
  },

  // ============ ACCOUNT FEATURES ============
  getAccountFeatures: async (accountId) => {
    try {
      const response = await api.get(`/banking/accounts/${accountId}/features/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching account features:', error);
      // Return default features if endpoint doesn't exist
      return {
        international_transfer_enabled: true,
        daily_transfer_limit: 10000,
        requires_verification: true,
        max_beneficiaries: 10
      };
    }
  },

  updateAccountFeatures: async (accountId, features) => {
    try {
      const response = await api.put(`/banking/accounts/${accountId}/features/`, features);
      return response.data;
    } catch (error) {
      console.error('Error updating account features:', error);
      throw error;
    }
  },

  // ============ ADMIN ENDPOINTS ============
  adminGetAllAccounts: async () => {
    try {
      const response = await api.get('/banking/accounts/');
      // Handle paginated response
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching all accounts:', error);
      return [];
    }
  },

  adminGetPendingTransfers: async () => {
    try {
      const response = await api.get('/banking/admin/pending-transfers/');
      // Handle paginated response
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching pending transfers:', error);
      return [];
    }
  },

  adminGenerateCodes: async (transferId) => {
    try {
      const response = await api.post(`/banking/admin/generate-codes/${transferId}/`);
      return response.data;
    } catch (error) {
      console.error('Error generating codes:', error);
      throw error;
    }
  },

  adminGetAccountFeatures: async (accountId) => {
    try {
      const response = await api.get(`/banking/admin/account-features/${accountId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin account features:', error);
      throw error;
    }
  },

  adminUpdateAccountFeatures: async (accountId, features) => {
    try {
      const response = await api.put(`/banking/admin/account-features/${accountId}/`, features);
      return response.data;
    } catch (error) {
      console.error('Error updating admin account features:', error);
      throw error;
    }
  },

  // ============ NEW ADMIN VERIFICATION ENDPOINTS ============
  adminGetUsersNeedingVerification: async () => {
    try {
      const response = await api.get('/banking/admin/users-needing-verification/');
      return response.data;
    } catch (error) {
      console.error('Error fetching users needing verification:', error);
      return { total_users_needing_codes: 0, by_step: {} };
    }
  },

  adminGenerateVerificationCode: async (userId, stepNumber) => {
    try {
      const response = await api.post('/banking/admin/generate-verification-code/', {
        user_id: userId,
        step_number: stepNumber
      });
      return response.data;
    } catch (error) {
      console.error('Error generating verification code:', error);
      throw error;
    }
  },

  adminGetVerificationDashboard: async () => {
    try {
      const response = await api.get('/banking/admin/verification-dashboard/');
      return response.data;
    } catch (error) {
      console.error('Error fetching verification dashboard:', error);
      throw error;
    }
  }
};

export default internationalTransferService;